import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from '@adsk/bim360-matrix-react-button';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import TemplatesTableContainer from './TemplatesTableContainer';
import TemplatesProjectGridHeaderContainer from './TemplatesProjectGridHeaderContainer';
import TemplatesFilterPanelContainer from './TemplatesFilterPanelContainer';
import TemplateCreateModalContainer from './TemplateCreateModalContainer';
import TemplatePreviewModalContainer from './TemplatePreviewModalContainer';
import TemplateDuplicateModalContainer from './TemplateDuplicateModalContainer';
import TemplateDeleteModalContainer from './TemplateDeleteModalContainer';
import {stopPropagation} from '../../utils/dom-helpers';
import {preloadedFilterQueryParamsState} from '../../constants/preloaded-states';
import {viewTypes} from '../../constants/view-types';
import {COLLECTION_TYPES} from '../../constants/issue-query-params';
import {templatesCollectionType, templatesQueryParamsType} from '../../constants/proptype-validation';
import i18n from '../../utils/i18n';
import TemplateCategoriesManageModalContainer from './TemplateCategoriesManageModalContainer';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';

class TemplatesProjectGrid extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isFilterPanelOpen: false
    };
  }

  componentDidMount() {
    if (!this.props.categoriesDidFetchOnce) {
      this.props.fetchIssueTemplateCategoriesAction();
    }
    this.fetchIssueTemplates();
  }

  componentDidUpdate(prevProps) {
    const {queryParams} = this.props;
    if (prevProps.queryParams !== queryParams) {
      this.fetchIssueTemplates();
    }
  }

  fetchIssueTemplates = () => {
    const {fetchIssueTemplatesAction, queryParams} = this.props;
    fetchIssueTemplatesAction(queryParams);
  };

  toggleFilterPanel = () => {
    this.setState({isFilterPanelOpen: !this.state.isFilterPanelOpen});
  };

  closeFilterPanel = () => {
    this.setState({isFilterPanelOpen: false});
  };

  getPreloadedQueryParamsState() {
    return preloadedFilterQueryParamsState[viewTypes.PROJECT][COLLECTION_TYPES.TEMPLATES];
  }

  resetFilters = event => {
    stopPropagation(event);
    this.props.setQueryParamsAction(this.getPreloadedQueryParamsState(), COLLECTION_TYPES.TEMPLATES);
  };

  returnToListView = () => {
    this.props.changeLocationAction(`project/${TYPE_QUALITY_ISSUES}/templates`);
  };

  render() {
    const {
      isShowNewTemplateModal,
      isTemplatesCollectionLoading,
      isTemplatesCollectionError,
      tableData,
      isFiltered,
      isSearched,
      changeLocationAction
    } = this.props;
    const {isFilterPanelOpen} = this.state;
    const showErrorState = isTemplatesCollectionError && !isTemplatesCollectionLoading;
    const showEmptyState =
      !isTemplatesCollectionError && !isTemplatesCollectionLoading && !tableData.length && !isFiltered && !isSearched;
    const showContentAndHeader = !isTemplatesCollectionError && !showEmptyState;

    return (
      <div
        className={cx('TemplatesProjectGrid', {
          'TemplatesProjectGrid--center': showErrorState || showEmptyState
        })}
      >
        {showErrorState && (
          <EmptyState
            icon={EmptyState.Icons.CONE}
            text={i18n.t('templatesGrid.emptyState.didntLoad')}
            footer={<Button onClick={this.fetchIssueTemplates} styleType={Button.StyleType.INLINE_LINK}>
              {i18n.t('templatesGrid.emptyState.tryAgain')}
            </Button>}
          />
        )}
        {showEmptyState && (
          <EmptyState
            icon={EmptyState.Icons.EMPTY_BOX}
            text={i18n.t('templatesGrid.emptyState.noTemplates')}
            footer={<Button
              onClick={() => changeLocationAction(`/project/${TYPE_QUALITY_ISSUES}/templates/new`)}
              styleType={Button.StyleType.PRIMARY}
            >
              {i18n.t('templatesGrid.emptyState.createTemplate')}
            </Button>}
          />
        )}
        {showContentAndHeader && (
          <TemplatesProjectGridHeaderContainer
            isFilterPanelOpen={isFilterPanelOpen}
            toggleFilterPanel={this.toggleFilterPanel}
            resetFilters={this.resetFilters}
          />
        )}
        {showContentAndHeader && (
          <div className="TemplatesProjectGrid__content">
            <TemplatesFilterPanelContainer
              isFilterPanelOpen={isFilterPanelOpen}
              closeFilterPanel={this.closeFilterPanel}
              resetFilters={this.resetFilters}
              container={this}
            />
            <div className="TemplatesProjectGrid__table-container">
              <TemplatesTableContainer />
            </div>
          </div>
        )}
        {isShowNewTemplateModal && (
          <TemplateCreateModalContainer container={this} onClose={this.returnToListView} isOpen />
        )}
        <TemplatePreviewModalContainer container={this} onClose={this.returnToListView} />
        <TemplateDuplicateModalContainer container={this} />
        <TemplateDeleteModalContainer container={this} />
        <TemplateCategoriesManageModalContainer container={this} />
      </div>
    );
  }
}

TemplatesProjectGrid.displayName = 'TemplatesProjectGrid';

TemplatesProjectGrid.propTypes = {
  isShowNewTemplateModal: PropTypes.bool.isRequired,
  categoriesDidFetchOnce: PropTypes.bool.isRequired,
  isTemplatesCollectionLoading: PropTypes.bool.isRequired,
  isTemplatesCollectionError: PropTypes.bool.isRequired,
  isFiltered: PropTypes.bool.isRequired,
  isSearched: PropTypes.bool.isRequired,
  tableData: templatesCollectionType.isRequired,
  queryParams: templatesQueryParamsType.isRequired,
  fetchIssueTemplateCategoriesAction: PropTypes.func.isRequired,
  fetchIssueTemplatesAction: PropTypes.func.isRequired,
  setQueryParamsAction: PropTypes.func.isRequired,
  changeLocationAction: PropTypes.func.isRequired
};

export default TemplatesProjectGrid;
